
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PuttingResult } from "@/types/puttingResults";
import { 
  processDriverDistanceData,
  getResultsByLevel,
  SwingTendency
} from './SwingChartUtils';
import DistancePieChart from './DistancePieChart';
import { Loader2 } from "lucide-react";

// Driver accuracy only - no distance specific data
const DRIVER_ACCURACY = {
  "driver": "driver_accuracy"
};

interface DriverDistanceChartsProps {
  driverResults: PuttingResult[];
  selectedLevel: string | null;
  tendencies: SwingTendency[];
}

const DriverDistanceCharts: React.FC<DriverDistanceChartsProps> = ({
  driverResults,
  selectedLevel,
  tendencies
}) => {
  const { t } = useTranslation('training');
  const [loading, setLoading] = useState(true);
  const [distanceData, setDistanceData] = useState<Record<string, any>>({});
  
  // For debugging
  useEffect(() => {
    console.log("Driver results count:", driverResults.length);
    console.log("Driver tendencies count:", tendencies.filter(t => t.club_type === 'driver').length);
    
    // Get only results based on selected level
    const driverFilteredResults = getResultsByLevel(driverResults, selectedLevel);
    
    // Process driver accuracy data
    const processDriverData = async () => {
      setLoading(true);
      
      const data: Record<string, any> = {};
      
      // Get data from the tendencies table - we don't care about specific distances
      const tendencyData = await processDriverDistanceData(
        tendencies, 
        "driver", 
        "driver_accuracy", 
        selectedLevel
      );
      
      data["driver"] = tendencyData;
      
      setDistanceData(data);
      setLoading(false);
    };
    
    processDriverData();
  }, [driverResults, tendencies, selectedLevel]);
  
  if (loading && Object.keys(distanceData).length === 0) {
    return (
      <div className="space-y-6 mb-8">
        <h3 className="text-lg font-bold text-gray-800 px-1 border-l-4 border-green-500 pl-3">{t('charts.driverTendencies')}</h3>
        <div className="flex justify-center items-center p-8">
          <Loader2 className="h-8 w-8 text-green-600 animate-spin" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6 mb-8">
      <h3 className="text-lg font-bold text-gray-800 px-1 border-l-4 border-green-500 pl-3">{t('charts.driverTendencies')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-6">
        <DistancePieChart
          key="driver-accuracy"
          title="Driver"
          distanceLabel="Précision"
          distanceData={distanceData["driver"] || { left: 0, center: 0, right: 0, total: 0, points: 0 }}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default DriverDistanceCharts;
